import { Fragment, useEffect, useState, useCallback } from 'react';
import { useToast } from '../../hooks/useToast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { canViewAgencies } from '../../utils/agencyAccess';
import ListLoadError from '../../components/admin/ListLoadError';
import styles from './CSVUploadsPage.module.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const statusColors = {
  completed: { color: '#22c55e', bg: '#f0fdf4' },
  processing: { color: '#f59e0b', bg: '#fffbeb' },
  failed: { color: '#ef4444', bg: '#fef2f2' },
  pending: { color: '#737373', bg: '#f9fafb' },
};

const badgeStyle = (status) => {
  const c = statusColors[status] || statusColors.pending;
  return {
    display: 'inline-flex', padding: '0.25rem 0.625rem', borderRadius: '6px',
    fontSize: '0.75rem', fontWeight: 600, color: c.color, backgroundColor: c.bg, whiteSpace: 'nowrap',
  };
};

// Card 53581 - the agency filter cannot use the full-width <ListLoadError> panel (that
// component is for list REGIONS, rendered below at the rows and table sites), but it must
// still distinguish "could not load" from "none exist". Inline style objects are this
// file's existing idiom (see badgeStyle above); the SHARED CSVUploadsPage.module.css is
// deliberately untouched so this change reverts as one self-contained file.
const agencyErrorStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
};

const agencyRetryStyle = {
  padding: '0.375rem 0.75rem', borderRadius: '6px',
  border: '0.5px solid var(--color-tan-400)', background: 'var(--color-tan-50)',
  color: 'var(--color-error)', fontSize: '0.75rem', fontWeight: 600,
  letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  cursor: 'pointer', transition: 'background 0.2s ease, border-color 0.2s ease',
};

const thStyle = {
  padding: '0.5rem', textAlign: 'left', fontWeight: 600,
  color: 'var(--color-gray-500)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em',
};

const EligibilityUploadsPage = () => {
  const { toast } = useToast();
  const [uploads, setUploads] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [agenciesError, setAgenciesError] = useState(false);

  const { hasPermission } = useAuth();

  const loadAgencies = useCallback(async () => {
    setAgenciesError(false);
    // Gated on the permission the API already enforces (card 49621): without
    // agencies.view this request can only ever 403, and the old catch reported
    // that denial as "no agencies / API unavailable", which is false on both counts.
    // A PREDICTED 403 is not a load failure, so this arm stays silent (card 53581).
    if (!canViewAgencies(hasPermission)) {
      setAgencies([]);
      return;
    }
    try {
      const response = await api.get('/admin/agencies');
      setAgencies(response.agencies || []);
    } catch {
      // Card 53581: `agencies` drives ONLY the filter below, whose sole static child is
      // "All Agencies". Leaving the array at [] on a real failure rendered that control
      // byte-identically to a tenant that has no partner agencies at all - the exact
      // confusion ListLoadError exists to end, on the one site that still had it.
      setAgencies([]);
      setAgenciesError(true);
    }
  }, [hasPermission]);

  const loadUploads = useCallback(async (page = 1, filters = {}) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.status) params.append('status', filters.status);
      if (filters.agencyId) params.append('agencyId', filters.agencyId);
      const response = await api.get(`/admin/eligibility-uploads?${params}`);
      setUploads(response.uploads || []);
      setPagination(response.pagination);
    } catch { setLoadError(true); setUploads([]); toast.error('Failed to load eligibility uploads'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadUploads(); loadAgencies(); }, [loadUploads, loadAgencies]);

  const getFilters = () => ({ status: statusFilter, agencyId: agencyFilter });

  const handleFilterChange = (field, value) => {
    const newFilters = { ...getFilters(), [field]: value };
    if (field === 'status') setStatusFilter(value);
    if (field === 'agencyId') setAgencyFilter(value);
    loadUploads(1, newFilters);
  };

  const loadRows = async (uploadId) => {
    setRowsLoading(true);
    setRowsError(false);
    try {
      const response = await api.get(`/admin/eligibility-uploads/${uploadId}/rows`);
      setExpandedRows(response.rows || []);
    } catch { toast.error('Failed to load upload rows'); setExpandedRows([]); setRowsError(true); }
    finally { setRowsLoading(false); }
  };

  const handleRowExpand = (uploadId) => {
    if (expandedId === uploadId) { setExpandedId(null); setExpandedRows([]); setRowsError(false); return; }
    setExpandedId(uploadId);
    loadRows(uploadId);
  };

  const hasActiveFilters = statusFilter || agencyFilter;
  const clearFilters = () => { setStatusFilter(''); setAgencyFilter(''); loadUploads(1, {}); };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Eligibility Uploads</h1>
          <p>Partner eligibility file upload history</p>
        </div>
      </div>

      <Card className={styles.filters}>
        <div className={styles.filterRow}>
          <select className={styles.select} value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}>
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="processing">Processing</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          {agenciesError ? (
            <span style={agencyErrorStyle} role="alert">
              <select className={styles.select} value="" disabled>
                <option value="">Agencies could not be loaded</option>
              </select>
              <button type="button" style={agencyRetryStyle} onClick={loadAgencies}>
                Retry
              </button>
            </span>
          ) : (
            <select className={styles.select} value={agencyFilter}
              onChange={(e) => handleFilterChange('agencyId', e.target.value)}>
              <option value="">All Agencies</option>
              {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          {hasActiveFilters && (
            <Button variant="outline" size="small" onClick={clearFilters}>Clear Filters</Button>
          )}
        </div>
      </Card>

      <Card className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}><LoadingSpinner /></div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th><th>Agency</th><th>Filename</th><th>Status</th>
                <th>Total</th><th>Success</th><th>Errors</th><th>Skipped</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((u) => (
                <Fragment key={u.id}>
                  <tr key={u.id} onClick={() => handleRowExpand(u.id)}
                    style={{ cursor: 'pointer', transition: 'background 0.2s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-tan-50)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td><span className={styles.uploadDate}>{formatDate(u.created_at)}</span></td>
                    <td>{u.agencyName ? <span className={styles.agencyBadge}>{u.agencyName}</span> : <span className={styles.noValue}>—</span>}</td>
                    <td><div className={styles.filename}>{u.filename || '—'}</div></td>
                    <td><span style={badgeStyle(u.status)}>{(statusColors[u.status] ? u.status.charAt(0).toUpperCase() + u.status.slice(1) : 'Pending')}</span></td>
                    <td><span className={styles.rowCount}>{(u.total_rows ?? 0).toLocaleString()}</span></td>
                    <td><span className={styles.rowCount} style={{ color: '#22c55e' }}>{(u.success_count ?? 0).toLocaleString()}</span></td>
                    <td><span className={styles.rowCount} style={{ color: u.error_count > 0 ? '#ef4444' : undefined }}>{(u.error_count ?? 0).toLocaleString()}</span></td>
                    <td><span className={styles.rowCount} style={{ color: u.skip_count > 0 ? '#f59e0b' : undefined }}>{(u.skip_count ?? 0).toLocaleString()}</span></td>
                  </tr>
                  {expandedId === u.id && (
                    <tr key={`${u.id}-detail`}>
                      <td colSpan={8} style={{ padding: 0, background: 'var(--color-tan-50)' }}>
                        {rowsLoading ? (
                          <div style={{ padding: '1.5rem', textAlign: 'center' }}><LoadingSpinner /></div>
                        ) : rowsError ? (
                          <ListLoadError message="Could not load upload rows" onRetry={() => loadRows(u.id)} />
                        ) : expandedRows.length === 0 ? (
                          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-gray-500)', fontSize: '0.875rem' }}>No row details available</div>
                        ) : (
                          <div style={{ padding: '0.75rem 1.25rem', maxHeight: '300px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                              <thead>
                                <tr>
                                  <th style={thStyle}>Row</th>
                                  <th style={thStyle}>Action</th>
                                  <th style={thStyle}>Status</th>
                                  <th style={thStyle}>Name</th>
                                  <th style={thStyle}>Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {expandedRows.map((r) => (
                                  <tr key={r.id} style={{ borderTop: '0.5px solid var(--color-tan-300)' }}>
                                    <td style={{ padding: '0.5rem', fontFamily: "'SF Mono', Monaco, monospace" }}>{r.row_number}</td>
                                    <td style={{ padding: '0.5rem' }}>{r.action || '—'}</td>
                                    <td style={{ padding: '0.5rem' }}><span style={badgeStyle(r.status)}>{r.status || '—'}</span></td>
                                    <td style={{ padding: '0.5rem' }}>{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
                                    <td style={{ padding: '0.5rem', color: '#ef4444', fontSize: '0.75rem' }}>{r.error_message || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
        {!loading && loadError && (
          <ListLoadError
            message="Could not load eligibility uploads"
            onRetry={() => loadUploads(1, getFilters())}
          />
        )}
        {!loading && !loadError && uploads.length === 0 && (
          <div className={styles.empty}>{hasActiveFilters ? 'No uploads match your filters' : 'No eligibility uploads yet'}</div>
        )}
      </Card>

      {pagination && pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <Button variant="outline" size="small" disabled={pagination.page <= 1}
            onClick={() => loadUploads(pagination.page - 1, getFilters())}>Previous</Button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <Button variant="outline" size="small" disabled={pagination.page >= pagination.totalPages}
            onClick={() => loadUploads(pagination.page + 1, getFilters())}>Next</Button>
        </div>
      )}
    </div>
  );
};

export default EligibilityUploadsPage;
